// @architect-approved: INFRASTRUCTURE - BugReporterFAB is a global UI infra component, not a DB-registered component
/**
 * BugReporterFAB - Quick Issue Reporter for Product Manager Navigation
 *
 * Persistent red floating action button (bottom-left) visible ONLY to TG SuperAdmins.
 * Allows quick issue reporting during navigation sessions with product managers.
 *
 * Features:
 * - Red FAB button (fixed position bottom-left)
 * - Context menu with issue categories
 * - Auto-captures current sequence number from URL
 * - Posts to Development_Backlog via API
 * - Restricted to SuperAdmin users only
 *
 * Created: 2026-06-25
 * @tg-seq N/A (infrastructure component, not routable)
 */

import React, { useState, useEffect } from 'react';
import {
    Fab,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
} from '@mui/material';
import {
    BugReport as BugReportIcon,
    ChangeCircle as RefactorIcon,
    CloudOff as DataLoadingIcon,
    TextFields as EncodingIcon,
    Route as RoutingIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { apiRequest } from '../utils/apiService';
import { SecureStorage } from '../utils/SecureStorage';
import { getUserAuthContext } from '../services/componentAuthService';

interface IssueCategory {
    key: string;
    label: string;
    icon: React.ReactElement;
    taskType: string;
}

const ISSUE_CATEGORIES: IssueCategory[] = [
    {
        key: 'refactor',
        label: 'Refactor to Placeholder',
        icon: <RefactorIcon />,
        taskType: 'UI_STYLING',
    },
    {
        key: 'data',
        label: 'Data Loading Problem',
        icon: <DataLoadingIcon />,
        taskType: 'BUG',
    },
    {
        key: 'encoding',
        label: 'Encoding Issues',
        icon: <EncodingIcon />,
        taskType: 'BUG',
    },
    {
        key: 'routing',
        label: 'Routing',
        icon: <RoutingIcon />,
        taskType: 'BUG',
    },
];

const BugReporterFAB: React.FC = () => {
    const location = useLocation();
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<IssueCategory | null>(null);
    const [description, setDescription] = useState('');
    const [currentSeq, setCurrentSeq] = useState<number | null>(null);
    const [currentPath, setCurrentPath] = useState('');

    // Check if user is SuperAdmin on mount and location change
    useEffect(() => {
        const userContext = getUserAuthContext();
        setIsSuperAdmin(userContext.is_tg_super_admin);

        // Extract sequence number from URL
        // Pattern: /component/10814 or /component/:seq
        const seqMatch = location.pathname.match(/\/component\/(\d+)/);
        if (seqMatch) {
            setCurrentSeq(parseInt(seqMatch[1], 10));
        } else {
            setCurrentSeq(null);
        }
        setCurrentPath(location.pathname);
    }, [location]);

    // Don't render if not SuperAdmin
    if (!isSuperAdmin) {
        return null;
    }

    const handleFABClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCategorySelect = (category: IssueCategory) => {
        setSelectedCategory(category);
        setDialogOpen(true);
        handleMenuClose();
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedCategory(null);
        setDescription('');
    };

    const handleSubmit = async () => {
        if (!selectedCategory) return;

        try {
            const pfuserUuid = SecureStorage.getPfuserUuid();
            if (!pfuserUuid) {
                toast.error('User not authenticated');
                return;
            }

            // Post to Development Backlog API
            await apiRequest('/api/development-backlog', 'POST', {
                manage: 1, // Create task
                TaskType: selectedCategory.taskType,
                TaskTitle: `${selectedCategory.label} - Seq ${currentSeq || 'Unknown'}`,
                TaskDescription:
                    description ||
                    `Issue reported during product manager navigation at ${currentPath}`,
                Component: currentSeq?.toString() || currentPath,
                UiComponentsChanged: currentPath,
                Status: 'BACKLOG',
                TaskPriority: 'MEDIUM',
                ArchitectUuid: pfuserUuid,
            });

            toast.success(`Issue reported: ${selectedCategory.label}`);
            handleDialogClose();
        } catch (error) {
            console.error('Failed to submit bug report:', error);
            toast.error('Failed to submit issue. Please try again.');
        }
    };

    return (
        <>
            {/* Red FAB Button (bottom-left, fixed position) */}
            <Fab
                color="error"
                aria-label="report issue"
                onClick={handleFABClick}
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    left: 16,
                    zIndex: 9999,
                    bgcolor: 'error.main',
                    '&:hover': {
                        bgcolor: 'error.dark',
                    },
                }}
            >
                <BugReportIcon />
            </Fab>

            {/* Issue Category Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                {ISSUE_CATEGORIES.map((category) => (
                    <MenuItem
                        key={category.key}
                        onClick={() => handleCategorySelect(category)}
                        sx={{ minWidth: 240 }}
                    >
                        <ListItemIcon>{category.icon}</ListItemIcon>
                        <ListItemText>{category.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>

            {/* Issue Details Dialog */}
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle>Report Issue: {selectedCategory?.label}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Current Component:{' '}
                            <strong>{currentSeq ? `Seq ${currentSeq}` : 'N/A'}</strong>
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Current Path: <strong>{currentPath}</strong>
                        </Typography>
                        <TextField
                            label="Additional Details (Optional)"
                            multiline
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe the issue in more detail..."
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="error"
                        startIcon={<BugReportIcon />}
                    >
                        Submit Issue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BugReporterFAB;
